import { useSetState } from '@mantine/hooks';

import { flip } from '@utils/flip';
import { useState } from 'react';

interface IFilterRequest {
  modelNames?: string[];
  manufactorNames?: string[];
  sourceNames?: string[];

  modelName?: string;
  modelNumber?: string;

  filterByEquipment?: boolean;
  manufacturerId?: string;
}
interface IFilterBase {
  sort?: string;
  direction?: 'ASC' | 'DESC' | undefined;
}
interface IPagination {
  limit: number;
}

export interface ISearchFilter<IFilter = IFilterRequest> {
  q: string;
  updatedAt: number;
  // Filter must be a partial.
  filter: Partial<IFilter>;
  sort: IFilterBase;
  pagination: IPagination;
}
const PAGE_LIMIT = 20;

const DIR_OPTS = ['ASC', 'DESC'] as const;

export interface IFilterFns<IFilter = IFilterRequest> {
  sortBy: (sortString: string, initial?: boolean) => void;
  filterBy: (names: IFilter) => void;
  reset: (kind: 'filter' | 'sort' | 'all') => void;
  searchBy: (term?: string) => void;
  refresh: VoidFunction;
}
export interface UseFilteredSearchReturn<IFilter = IFilterRequest> {
  filterFns: IFilterFns<IFilter>;
  pageFilter: ISearchFilter<IFilter>;
}

/**
 * Meant to provide a better api for search by filter
 *
 * # Note
 * This gives fns that can be used in combination with react query
 */
export function useFilteredSearch<IFilter = IFilterRequest>(
  defaultFilterOpts: Partial<ISearchFilter<IFilter>> = {},
): UseFilteredSearchReturn<IFilter> {
  const [pageFilter, setPageFilter] = useSetState<ISearchFilter<IFilter>>({
    q: defaultFilterOpts.q ?? '',
    updatedAt: defaultFilterOpts.updatedAt ?? 0,
    filter: {
      ...defaultFilterOpts.filter,
    },
    pagination: {
      limit: PAGE_LIMIT,
      ...defaultFilterOpts.pagination,
    },
    sort: {
      ...defaultFilterOpts.sort,
    },
  });

  const [filterFns] = useState(makeFilterFns(setPageFilter));
  return { filterFns, pageFilter };
}

type Fn<Args, Return = void> = (value: Args) => Return;
type PartialSetter<T> = Fn<Partial<T> | Fn<T, Partial<T>>>;

/**
 * # Note
 *
 * This is a pure function as it takes in a stable reference and returns functions that only work on that stable reference.
 */
function makeFilterFns<IFilter = IFilterRequest>(
  setPageFilter: PartialSetter<ISearchFilter<IFilter>>,
): IFilterFns<IFilter> {
  function sortBy(sortString: string, initial?: boolean) {
    const now = Date.now();
    setPageFilter(prev => {
      let dir = flip(prev.sort?.direction, DIR_OPTS);
      if (initial) {
        dir = flip(undefined, DIR_OPTS);
      }
      let sort: IFilterBase = {};
      if (dir) {
        sort = {
          sort: sortString,
          direction: dir,
        };
      }
      return {
        sort,
        updatedAt: now,
      };
    });
  }
  function filterBy(names: IFilter) {
    const now = Date.now();
    setPageFilter({
      filter: names,
      updatedAt: now,
    });
  }
  function searchBy(term = '') {
    const now = Date.now();

    setPageFilter({
      q: term,
      updatedAt: now,
    });
  }
  function reset(kind: 'filter' | 'sort' | 'all') {
    const now = Date.now();
    if (kind === 'filter') {
      setPageFilter({
        filter: {},
        updatedAt: now,
      });
    }

    if (kind === 'sort') {
      setPageFilter({
        sort: {},
        updatedAt: now,
      });
    }
    if (kind === 'all') {
      reset('sort');
      reset('filter');
    }
  }
  function refresh() {
    const now = Date.now();
    setPageFilter({
      updatedAt: now,
    });
  }

  return {
    sortBy,
    filterBy,
    reset,
    searchBy,
    refresh,
  };
}
